import { ValidationRule, applyRules, ValidationResult } from './validationRules';

interface FormField {
  value: string;
  error: string | null;
}

export class BaseForm {
  fields: Record<string, FormField>;
  initialValues: Record<string, string>; 

  constructor(initialFields: Record<string, string>) {
    this.initialValues = { ...initialFields };
    this.fields = Object.keys(initialFields).reduce((acc, fieldName) => {
      acc[fieldName] = { value: initialFields[fieldName], error: null };
      return acc;
    }, {} as Record<string, FormField>);
  }

  // Set field value
  setFieldValue(fieldName: string, value: string) {
    if (this.fields[fieldName]) {
      this.fields[fieldName].value = value;
      this.fields[fieldName].error = null; // Reset error on value change
    }
  }

  // Validate a single field
  validateField(fieldName: string, fieldNameDisplay: string, rules: ValidationRule[]): ValidationResult {
    const field = this.fields[fieldName];
    if (!field) return { isValid: true };

    const validation = applyRules(field.value, fieldNameDisplay, rules);
    field.error = (!validation.isValid && validation.errorMessage) ? validation.errorMessage : null;

    return validation;
  }

  // Validate all fields
  validateAll(rulesMap: Record<string, { displayName: string; rules: ValidationRule[] }>): boolean {
    let isFormValid = true;

    Object.keys(rulesMap).forEach((fieldName) => {
      const { displayName, rules } = rulesMap[fieldName];
      const validation = this.validateField(fieldName, displayName, rules);
      if (!validation.isValid) {
        isFormValid = false;
      }
    });

    return isFormValid;
  }

  // Get form state (values and errors)
  getFormState() {
    return {
      values: Object.keys(this.fields).reduce((acc, fieldName) => {
        acc[fieldName] = this.fields[fieldName].value;
        return acc;
      }, {} as Record<string, string>),
      errors: Object.keys(this.fields).reduce((acc, fieldName) => {
        acc[fieldName] = this.fields[fieldName].error;
        return acc;
      }, {} as Record<string, string | null>),
    };
  }

  reset() {
    Object.keys(this.fields).forEach((fieldName) => {
      this.fields[fieldName].value = this.initialValues[fieldName] || '';
      this.fields[fieldName].error = null;
    });
  }
}
